import React from 'react';
import { ClientDateHandler } from '../clientDateHandler/ClientDateHandler';
import InputLabel from '../inputLabel/InputLabel';

export type DateTimeDisplayProps = {
    name: string;
    //If useJson is used then it should be provided as DateTimeJsonFormat, if not then a string should be provided
    value?: string | Date;
    className?: string;
    label?: string;
    useJson?: boolean;
    UTC?: boolean;
    includeTime?: boolean;
    hasLabel?: boolean;
    hasBox?: boolean;
    /** Input width - Defaults to 150px but can be provided to make it bigger or smaller */
    inputWidth?: string;
};

/** A component used to display a date/time in UTC or not. The time can be removed and a box styling can be added or removed */
const DateTimeDisplay = ({
    name,
    value,
    className = '',
    label,
    useJson = false,
    UTC = false,
    includeTime = false,
    hasLabel = false,
    hasBox = false,
    inputWidth
}: DateTimeDisplayProps) => {
    const getDateFromJSON = (jsonText: string): Date => {
        const cdh = new ClientDateHandler();
        cdh.parseJSON(jsonText);

        const date = cdh.getLocalDate();
        return date as Date;
    };

    let dateValue: Date | undefined = undefined;

    if (useJson) {
        dateValue = getDateFromJSON(value as string);
        //A little worried about this check
    } else {
        dateValue = getDateValue(value as string);
    }

    let dateTextValue = '';
    if (UTC) dateTextValue = getUTCDisplayFormattedDate(dateValue, includeTime);
    else dateTextValue = getDisplayFormattedDate(dateValue, includeTime);

    const styleWrapper = `field ${className}`;

    const createLabel = (): JSX.Element => {
        if (hasLabel && label !== undefined) {
            return <InputLabel labelText={label} name={name} />;
        }
        return <></>;
    };

    const labelControl = createLabel();

    let dateTimeControl: JSX.Element;
    if (!hasBox) {
        dateTimeControl = <div className='form-group'>{dateTextValue}</div>;
    } else
        dateTimeControl = (
            <div className='form-group'>
                {labelControl}
                <div className={styleWrapper}>
                    <input
                        style={{ width: inputWidth }}
                        aria-label={name}
                        type='text'
                        readOnly={true}
                        className='form-control'
                        name={name}
                        placeholder={label}
                        value={dateTextValue}
                        role='input'
                    />
                </div>
            </div>
        );

    return dateTimeControl;
};

export default DateTimeDisplay;

const leftPad = (value: number): string | number => {
    if (value < 10) return '0' + value;
    else return value;
};

export const getISOFormattedDate = (
    dateVal: Date | undefined,
    includeTime = true,
    useUtc = true
): string => {
    if (dateVal === undefined) return Date.now().toString();

    let dateValue = null;
    if (typeof dateVal === 'number') {
        // dateVal = milliseconds
        dateValue = new Date(dateVal);
    } else {
        if (typeof dateVal === 'string') {
            // dateVal = milliseconds
            dateValue = new Date(dateVal);
        } else {
            dateValue = dateVal;
        }
    }

    let years = -1;
    if (useUtc) years = dateValue.getUTCFullYear();
    else years = dateValue.getFullYear();

    let month = -1;
    if (useUtc) month = dateValue.getUTCMonth() + 1;
    else month = dateValue.getMonth() + 1;

    let day = -1;
    if (useUtc) day = dateValue.getUTCDate();
    else day = dateValue.getDate();

    let hours = -1;
    if (useUtc) hours = dateValue.getUTCHours();
    else hours = dateValue.getHours();

    let minutes = -1;
    if (useUtc) minutes = dateValue.getUTCMinutes();
    else minutes = dateValue.getMinutes();

    let seconds = -1;
    if (useUtc) seconds = dateValue.getUTCSeconds();
    else seconds = dateValue.getSeconds();

    if (isNaN(day) || isNaN(month) || isNaN(years))
        return Date.now().toString();

    let formattedText = years + '-' + leftPad(month) + '-' + leftPad(day);
    if (includeTime)
        formattedText =
            formattedText +
            'T' +
            leftPad(hours) +
            ':' +
            leftPad(minutes) +
            ':' +
            leftPad(seconds) +
            'Z';

    return formattedText;
};

export const getDisplayFormattedDate = (
    dateValue: Date | undefined,
    includeTime = true
): string => {
    if (dateValue === undefined) return Date.now().toString();

    const day = dateValue.getDate();
    const month = dateValue.getMonth() + 1;
    const years = dateValue.getFullYear();

    const hours = dateValue.getHours();
    const minutes = dateValue.getMinutes();
    const seconds = dateValue.getSeconds();

    if (isNaN(day) || isNaN(month) || isNaN(years)) return '';

    let formattedText = leftPad(day) + '/' + leftPad(month) + '/' + years;
    if (includeTime)
        formattedText =
            formattedText +
            (' ' +
                leftPad(hours) +
                ':' +
                leftPad(minutes) +
                ':' +
                leftPad(seconds));

    return formattedText;
};

export const getUTCDisplayFormattedDate = (
    dateValue: Date | undefined,
    includeTime = true
): string => {
    if (dateValue === undefined) return Date.now().toString();

    const day = dateValue.getUTCDate();
    const month = dateValue.getUTCMonth() + 1;
    const years = dateValue.getUTCFullYear();

    const hours = dateValue.getUTCHours();
    const minutes = dateValue.getUTCMinutes();
    const seconds = dateValue.getUTCSeconds();

    if (isNaN(day) || isNaN(month) || isNaN(years)) return '';

    let formattedText = leftPad(day) + '/' + leftPad(month) + '/' + years;
    if (includeTime)
        formattedText =
            formattedText +
            (', ' +
                leftPad(hours) +
                ':' +
                leftPad(minutes) +
                ':' +
                leftPad(seconds));

    return formattedText;
};

export const getDateValue = (dateVal: string | number): Date => {
    let dateValue: Date | undefined = undefined;

    if (typeof dateVal === 'number') {
        // dateVal = milliseconds
        dateValue = new Date(dateVal);
    } else {
        if (typeof dateVal === 'string') {
            // dateVal = milliseconds
            dateValue = new Date(dateVal);
        } else {
            dateValue = dateVal;
        }
    }

    return dateValue;
};
